import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, X, Cloud, CheckCircle2, AlertTriangle, Archive } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";

import { useAdminPermissions } from "@/admin/PermissionsContext";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";
import { api, formatApiError } from "@/api/client";

/* Post pricing-simplification (2026-06):
 * - Only Free + Premium are active. Legacy Member row is kept in the table
 *   for audit but rendered greyed out with a "Deprecated" badge.
 * - Free plan edit modal: Stripe Price ID fields hidden.
 * - Premium plan edit modal: full UI, admin pastes real price_xxx IDs.
 * - Deprecated plan edit modal: Stripe Price ID fields disabled with a
 *   tooltip explaining the plan is retired.
 */
const PLAN_CODES = ["free", "premium"];
const TRIAL_DAY_OPTIONS = [0, 7, 14, 30];

function cents(c) {
  return `$${(Number(c || 0) / 100).toFixed(2)}`;
}

function PlanEditModal({ open, plan, onClose, onSaved, isCreate }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("free");
  const [priceCents, setPriceCents] = useState(0);
  const [priceAnnualCents, setPriceAnnualCents] = useState("");
  const [accessLevel, setAccessLevel] = useState("free");
  const [features, setFeatures] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  // Phase Stripe-1 fields
  const [stripePriceMonthly, setStripePriceMonthly] = useState("");
  const [stripePriceAnnual, setStripePriceAnnual] = useState("");
  const [trialDays, setTrialDays] = useState(0);
  const [annualDiscount, setAnnualDiscount] = useState(17);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setName(""); setDescription(""); setCode("free"); setPriceCents(0);
      setPriceAnnualCents(""); setAccessLevel("free"); setFeatures("");
      setIsActive(true); setIsPaid(false);
      setStripePriceMonthly(""); setStripePriceAnnual("");
      setTrialDays(0); setAnnualDiscount(17); setDisplayOrder(0);
    } else if (plan) {
      setName(plan.name || "");
      setDescription(plan.description || "");
      setCode(plan.code || "free");
      setPriceCents(plan.price_monthly_cents || 0);
      setPriceAnnualCents(plan.price_annual_cents != null ? String(plan.price_annual_cents) : "");
      setAccessLevel(plan.access_level || "free");
      setFeatures((plan.features || []).join("\n"));
      setIsActive(!!plan.is_active);
      setIsPaid(!!plan.is_paid);
      setStripePriceMonthly(plan.stripe_price_id_monthly || "");
      setStripePriceAnnual(plan.stripe_price_id_annual || "");
      setTrialDays(Number(plan.trial_days || 0));
      setAnnualDiscount(Number(plan.annual_discount_percent ?? 17));
      setDisplayOrder(Number(plan.display_order ?? plan.sort_priority ?? 0));
    }
  }, [open, plan, isCreate]);

  if (!open) return null;

  const isDeprecated = !!(plan?.deprecated_at);
  const isFreeTier = code === "free" || plan?.code === "free";
  // Post pricing-simplification, Free and Premium are fixed system tiers —
  // their `is_paid` flag is a fact of the tier design, not an admin choice.
  // Lock the checkbox so a stray click can't demote Premium to a free-like
  // state and destroy the /pricing → Subscribe flow.
  const isFixedTier = ["free", "premium"].includes(code) || ["free", "premium"].includes(plan?.code);
  // Stripe fields visible ONLY for a paid, non-deprecated, non-free plan.
  const showStripeFields = !isFreeTier;
  // Fields are read-only when the plan is deprecated.
  const stripeFieldsDisabled = isDeprecated;

  async function save() {
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        price_monthly_cents: Number(priceCents) || 0,
        price_annual_cents: priceAnnualCents === "" ? null : (Number(priceAnnualCents) || null),
        access_level: accessLevel,
        features: features.split("\n").map(s => s.trim()).filter(Boolean),
        is_active: isActive,
        stripe_price_id_monthly: stripePriceMonthly.trim() || null,
        stripe_price_id_annual: stripePriceAnnual.trim() || null,
        trial_days: Number(trialDays) || 0,
        annual_discount_percent: Number(annualDiscount) || 0,
        display_order: Number(displayOrder) || 0,
      };
      // is_paid is only writable when the tier isn't a fixed system tier.
      // For Free/Premium we omit the field so the backend `exclude_unset=True`
      // partial-update path leaves the DB value untouched.
      if (!isFixedTier) {
        body.is_paid = isPaid;
      }
      // Debug breadcrumb — logged to browser devtools so the operator can
      // see exactly what the modal is sending on Save. Kept lightweight
      // (no PII in Plan payloads) so it's safe to leave enabled.
      // eslint-disable-next-line no-console
      console.info("[admin/plans] Save → PATCH body:", body);
      let resp;
      if (isCreate) {
        resp = await api.post("/admin/plans", { ...body, code });
        toast.success("Plan created");
      } else {
        resp = await api.patch(`/admin/plans/${plan.id}`, body);
        // Show which Stripe IDs actually landed so a mis-paste is impossible
        // to miss (previous "Plan updated" was too generic — didn't tell the
        // admin whether the Price IDs went through).
        const m = resp?.data?.stripe_price_id_monthly;
        const a = resp?.data?.stripe_price_id_annual;
        if (plan.code !== "free" && (m || a)) {
          toast.success(`Plan updated — Stripe IDs: monthly=${m || "—"}, annual=${a || "—"}`);
        } else {
          toast.success("Plan updated");
        }
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[admin/plans] Save failed:", e?.response?.status, e?.response?.data);
      toast.error(formatApiError(e));
    }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="plan-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.65)" }} onClick={onClose}>
      <div className="w-full max-w-xl rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] p-6 max-h-[92vh] overflow-y-auto" style={{ background: "var(--ds-color-surface)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="font-display text-xl text-[var(--ds-color-ink)]">{isCreate ? "Create plan" : `Edit plan: ${plan?.name}`}</h2>
            {isDeprecated && (
              <div
                data-testid="plan-modal-deprecated-note"
                className="mt-1 text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-danger)]"
              >
                Deprecated · retired {new Date(plan.deprecated_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <button data-testid="plan-modal-close" onClick={onClose} className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          {isCreate && (
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Code</label>
              <select data-testid="plan-input-code" value={code} onChange={(e) => setCode(e.target.value)} className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)]" style={{ background: "var(--ds-color-surface)" }}>
                {PLAN_CODES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Name</label>
            <input data-testid="plan-input-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)]" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Description</label>
            <textarea data-testid="plan-input-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Price (cents/month)</label>
              <input type="number" min={0} data-testid="plan-input-price" value={priceCents} onChange={(e) => setPriceCents(e.target.value)} className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)]" />
              <div className="text-[11px] text-[var(--ds-color-ink-muted)] mt-1">= {cents(priceCents)}</div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Access level</label>
              <select data-testid="plan-input-access-level" value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)} className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)]" style={{ background: "var(--ds-color-surface)" }}>
                {["free", "premium"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--ds-color-ink-muted)]">
            <input
              data-testid="plan-input-is-paid"
              type="checkbox"
              checked={isPaid}
              onChange={(e) => setIsPaid(e.target.checked)}
              disabled={isDeprecated || isFixedTier}
            />
            Paid tier (requires Stripe checkout)
            {isFixedTier && !isDeprecated && (
              <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
                — locked (fixed tier)
              </span>
            )}
          </label>

          {/* Stripe linkage panel — hidden for Free tier, disabled for Deprecated */}
          {showStripeFields && (
            <div
              data-testid="plan-modal-stripe-panel"
              className="rounded-md p-3 border border-[var(--ds-color-admin-border)]"
              style={{ background: "var(--ds-color-accent-soft)", opacity: stripeFieldsDisabled ? 0.55 : 1 }}
              title={stripeFieldsDisabled ? "Plan deprecated — cannot re-enable via UI. Contact ops to reactivate." : undefined}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ds-color-accent)]">Stripe linkage</div>
                {stripeFieldsDisabled && (
                  <div
                    data-testid="plan-modal-stripe-disabled-hint"
                    className="text-[10px] text-[var(--ds-color-danger)]"
                  >
                    Deprecated — read-only
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Stripe Price ID — Monthly</label>
                  <input
                    data-testid="plan-input-stripe-price-monthly"
                    placeholder="price_1QwMonthly..."
                    value={stripePriceMonthly}
                    onChange={(e) => setStripePriceMonthly(e.target.value)}
                    disabled={stripeFieldsDisabled}
                    className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm font-mono text-[var(--ds-color-ink)] disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Stripe Price ID — Annual</label>
                  <input
                    data-testid="plan-input-stripe-price-annual"
                    placeholder="price_1QwAnnual..."
                    value={stripePriceAnnual}
                    onChange={(e) => setStripePriceAnnual(e.target.value)}
                    disabled={stripeFieldsDisabled}
                    className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm font-mono text-[var(--ds-color-ink)] disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">
                    Annual price (cents) — optional override
                  </label>
                  <input
                    type="number" min={0}
                    data-testid="plan-input-price-annual-cents"
                    value={priceAnnualCents}
                    placeholder="e.g. 14900 for $149"
                    onChange={(e) => setPriceAnnualCents(e.target.value)}
                    disabled={stripeFieldsDisabled}
                    className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)] disabled:cursor-not-allowed"
                  />
                  <div className="text-[10px] text-[var(--ds-color-ink-muted)] mt-1">
                    Leave blank to derive from monthly × 12 × (1 − discount%).
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Trial days</label>
                  <select
                    data-testid="plan-input-trial-days"
                    value={trialDays}
                    onChange={(e) => setTrialDays(Number(e.target.value))}
                    disabled={stripeFieldsDisabled}
                    className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)] disabled:cursor-not-allowed"
                    style={{ background: "var(--ds-color-surface)" }}
                  >
                    {TRIAL_DAY_OPTIONS.map(n => <option key={n} value={n}>{n === 0 ? "No trial" : `${n} days`}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Annual discount %</label>
                  <input
                    type="number" min={0} max={50}
                    data-testid="plan-input-annual-discount"
                    value={annualDiscount}
                    onChange={(e) => setAnnualDiscount(e.target.value)}
                    disabled={stripeFieldsDisabled}
                    className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)] disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Display order</label>
                  <input
                    type="number"
                    data-testid="plan-input-display-order"
                    value={displayOrder}
                    onChange={(e) => setDisplayOrder(e.target.value)}
                    className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)]"
                  />
                </div>
              </div>
              <p className="text-[10px] text-[var(--ds-color-ink-muted)] mt-2 leading-relaxed">
                Paste the exact Stripe Price IDs (not Product IDs). Find them under
                <span className="font-mono"> Stripe → Products → [plan] → Pricing</span>. Free tier doesn't need IDs.
              </p>
            </div>
          )}

          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Features (one per line)</label>
            <textarea data-testid="plan-input-features" rows={5} value={features} onChange={(e) => setFeatures(e.target.value)} className="w-full rounded-[var(--ds-radius-input)] border border-[var(--ds-color-input-border)] bg-[var(--ds-color-surface)] px-3 py-2 text-sm text-[var(--ds-color-ink)] font-mono" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--ds-color-ink-muted)]">
            <input data-testid="plan-input-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active (visible on /pricing)
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-5">
          <button onClick={onClose} className="rounded-md border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-sm text-[var(--ds-color-ink-muted)]">Cancel</button>
          <button data-testid="plan-modal-save" disabled={busy} onClick={save} className="rounded-md px-3.5 py-1.5 text-sm font-medium disabled:opacity-60" style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function CheckoutReadyBadge({ plan }) {
  // Phase Stripe-1 P0 — backend now computes `checkout_readiness` per plan
  // with a real Stripe reachability probe. Prefer that over the client-side
  // regex heuristic so we can't false-positive on a broken key.
  const r = plan.checkout_readiness;
  if (r) {
    const status = r.status;
    const reason = r.reason || "";
    if (status === "deprecated") {
      return (
        <span
          data-testid={`plan-checkout-badge-${plan.code}`}
          title={reason}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
          style={{ background: "var(--ds-color-admin-border)", color: "var(--ds-color-ink-muted)" }}
        >
          <Archive className="w-3 h-3" /> Deprecated
        </span>
      );
    }
    if (status === "not_paid") {
      return (
        <span
          data-testid={`plan-checkout-badge-${plan.code}`}
          title={reason}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
          style={{ background: "var(--ds-color-admin-border)", color: "var(--ds-color-ink-muted)" }}
        >
          N/A
        </span>
      );
    }
    if (status === "ready") {
      return (
        <span
          data-testid={`plan-checkout-badge-${plan.code}`}
          title={reason}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
          style={{ background: "var(--ds-color-success-soft)", color: "var(--ds-color-success)" }}
        >
          <CheckCircle2 className="w-3 h-3" /> Ready for checkout
        </span>
      );
    }
    if (status === "stripe_unreachable") {
      // Extract a short reason word for the badge label; full reason lives in tooltip.
      const shortReason = /invalid_key/i.test(reason)
        ? "invalid_key"
        : /timeout|unreachable|network/i.test(reason)
        ? "unreachable"
        : /no stripe api key|key_missing/i.test(reason)
        ? "no key"
        : "unreachable";
      return (
        <span
          data-testid={`plan-checkout-badge-${plan.code}`}
          title={reason}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
          style={{ background: "var(--ds-color-danger-soft)", color: "var(--ds-color-danger)" }}
        >
          <AlertTriangle className="w-3 h-3" /> Stripe unreachable — {shortReason}
        </span>
      );
    }
    if (status === "invalid_price_id_format") {
      return (
        <span
          data-testid={`plan-checkout-badge-${plan.code}`}
          title={reason}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
          style={{ background: "var(--ds-color-danger-soft)", color: "var(--ds-color-danger)" }}
        >
          <AlertTriangle className="w-3 h-3" /> Invalid Price ID format
        </span>
      );
    }
    // Default (missing_price_ids or anything else amber-worthy).
    return (
      <span
        data-testid={`plan-checkout-badge-${plan.code}`}
        title={reason}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
        style={{ background: "var(--ds-color-warning-soft)", color: "var(--ds-color-warning)" }}
      >
        <AlertTriangle className="w-3 h-3" /> Missing Stripe Price IDs
      </span>
    );
  }
  // ---- Legacy fallback (older backend that doesn't emit checkout_readiness) ----
  if (plan.deprecated_at) {
    return (
      <span
        data-testid={`plan-checkout-badge-${plan.code}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
        style={{ background: "var(--ds-color-admin-border)", color: "var(--ds-color-ink-muted)" }}
      >
        <Archive className="w-3 h-3" /> Deprecated
      </span>
    );
  }
  if (plan.code === "free" || plan.is_paid === false) {
    return (
      <span
        data-testid={`plan-checkout-badge-${plan.code}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
        style={{ background: "var(--ds-color-admin-border)", color: "var(--ds-color-ink-muted)" }}
      >
        N/A
      </span>
    );
  }
  const STRIPE_PRICE_RE = /^price_[a-zA-Z0-9]{14,}$/;
  const ok = (v) => typeof v === "string" && v.length > 0 && !/PLACEHOLDER/i.test(v) && STRIPE_PRICE_RE.test(v);
  if (ok(plan.stripe_price_id_monthly) && ok(plan.stripe_price_id_annual)) {
    return (
      <span
        data-testid={`plan-checkout-badge-${plan.code}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
        style={{ background: "var(--ds-color-success-soft)", color: "var(--ds-color-success)" }}
      >
        <CheckCircle2 className="w-3 h-3" /> Ready for checkout
      </span>
    );
  }
  return (
    <span
      data-testid={`plan-checkout-badge-${plan.code}`}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
      style={{ background: "var(--ds-color-warning-soft)", color: "var(--ds-color-warning)" }}
    >
      <AlertTriangle className="w-3 h-3" /> Missing Stripe Price IDs
    </span>
  );
}

export default function AdminPlans() {
  const { isOwner } = useAdminPermissions();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [isCreate, setIsCreate] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // null | plan
  const [deleteBusy, setDeleteBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // Backend already sorts: active-first (by display_order), then deprecated.
      const { data } = await api.get("/admin/plans");
      setPlans(data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function syncStripe(id) {
    try {
      const { data } = await api.post(`/admin/plans/${id}/sync-stripe`);
      toast.success(data?.stub ? "Synced (stub IDs assigned)" : "Synced to Stripe");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function del(p) {
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/plans/${p.id}`);
      toast.success("Plan deleted");
      setConfirmingDelete(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  const canWrite = isOwner;

  return (
    <AdminLayout
      title="Plans"
      subtitle="Pricing tiers exposed on /pricing and used for Stripe checkout"
      actions={canWrite ? (
        <button data-testid="plans-new-btn" onClick={() => { setEditPlan(null); setIsCreate(true); setModalOpen(true); }} className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium" style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}>
          <Plus className="w-3.5 h-3.5" /> New plan
        </button>
      ) : null}
    >
      <div className="mb-4 max-w-3xl">
        <AdminPageHelp
          title="Plans"
          purpose="Manage subscription plan tiers (name, price, features, Stripe price ID mapping)."
          configures="Plan display name, monthly/annual pricing, feature bullets, sort order, active status."
          impact="Live members see updated plan copy immediately on the pricing page. Existing subscriptions are unaffected."
          safety="Plans can be deactivated without deletion. Deletion is blocked if any active subscription is still on that plan."
        />
      </div>
      <PlanEditModal open={modalOpen} plan={editPlan} isCreate={isCreate} onClose={() => setModalOpen(false)} onSaved={reload} />
      {loading ? (
        <div data-testid="plans-loading" className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div>
      ) : (
        <div data-testid="plans-table" className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] overflow-hidden" style={{ background: "var(--ds-color-surface)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Price/mo</th>
                <th className="px-4 py-3 font-medium">Trial</th>
                <th className="px-4 py-3 font-medium">Annual −%</th>
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium">Stripe</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => {
                const deprecated = !!p.deprecated_at;
                return (
                  <tr
                    key={p.id}
                    data-testid={`plan-row-${p.code}`}
                    className="border-t border-[var(--ds-color-admin-border)]"
                    style={deprecated ? { opacity: 0.55 } : undefined}
                  >
                    <td className="px-4 py-3 text-[var(--ds-color-ink)] font-mono text-xs">{p.code}</td>
                    <td className="px-4 py-3 text-[var(--ds-color-ink)]">
                      <div className="flex items-center gap-2">
                        <span>{p.name}</span>
                        {deprecated && (
                          <span
                            data-testid={`plan-deprecated-badge-${p.code}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5"
                            style={{ background: "var(--ds-color-admin-border)", color: "var(--ds-color-ink-muted)" }}
                            title={p.deprecation_reason || "Deprecated"}
                          >
                            <Archive className="w-3 h-3" /> Deprecated
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[var(--ds-color-ink-muted)]">{cents(p.price_monthly_cents)}</td>
                    <td data-testid={`plan-trial-${p.code}`} className="px-4 py-3 text-[var(--ds-color-ink-muted)] text-xs">
                      {Number(p.trial_days || 0) > 0 ? `${p.trial_days}d` : "—"}
                    </td>
                    <td data-testid={`plan-annual-discount-${p.code}`} className="px-4 py-3 text-[var(--ds-color-ink-muted)] text-xs">
                      {Number(p.annual_discount_percent ?? 17)}%
                    </td>
                    <td data-testid={`plan-display-order-${p.code}`} className="px-4 py-3 text-[var(--ds-color-ink-muted)] text-xs">
                      {Number(p.display_order ?? p.sort_priority ?? 0)}
                    </td>
                    <td className="px-4 py-3">
                      <CheckoutReadyBadge plan={p} />
                    </td>
                    <td className="px-4 py-3">
                      {p.is_active ? <span className="text-[var(--ds-color-success)] text-xs">Active</span> : <span className="text-[var(--ds-color-ink-muted)] text-xs">Inactive</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {canWrite && (
                          <>
                            <button data-testid={`plan-edit-${p.code}`} onClick={() => { setEditPlan(p); setIsCreate(false); setModalOpen(true); }} className="rounded border border-[var(--ds-color-admin-border)] px-2 py-1 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]">Edit</button>
                            {!deprecated && p.code !== "free" && (
                              <button data-testid={`plan-sync-${p.code}`} onClick={() => syncStripe(p.id)} className="inline-flex items-center gap-1 rounded border border-[var(--ds-color-admin-border)] px-2 py-1 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]">
                                <Cloud className="w-3 h-3" /> Sync
                              </button>
                            )}
                            {p.code !== "free" && !deprecated && (
                              <button data-testid={`plan-delete-${p.code}`} onClick={() => setConfirmingDelete(p)} aria-label={`Delete plan ${p.code}`} className="inline-flex items-center gap-1 rounded border border-[var(--ds-color-danger)] px-2 py-1 text-xs text-[var(--ds-color-danger)] hover:bg-[var(--ds-color-danger-soft)]">
                                <Trash2 className="w-3 h-3" aria-hidden="true" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmingDelete}
        onCancel={() => setConfirmingDelete(null)}
        onConfirm={() => confirmingDelete && del(confirmingDelete)}
        busy={deleteBusy}
        variant="destructive"
        title={confirmingDelete ? `Delete plan "${confirmingDelete.name}"?` : "Delete plan"}
        description="This is only allowed if no active subscriptions reference this plan. Historical subscription records that used this plan are preserved."
        confirmLabel="Delete plan"
        testId="plans-delete-confirm"
      />
    </AdminLayout>
  );
}
